import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  Mail, Plus, Search, Filter, Edit3, Trash2, Eye, Save, RefreshCw,
  CheckCircle, AlertTriangle, X, Target, Activity, Users, MessageSquare,
  Calendar, FileText, Database, Server, Shield, Bell, Download, Upload,
  Settings, TestTube, BarChart3, TrendingUp, PieChart, Globe, Lock,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, MoreHorizontal, MoreVertical, Copy,
  ExternalLink, Clock, Zap, Star, Heart, Coffee, Pizza, Cake, Music,
  Camera, Video, File, Share, Bookmark, Flag, Monitor, Cpu, HardDrive,
  Network, Wifi, Signal, Battery, Volume, Volume2, VolumeX, Play,
  Pause, Stop, SkipBack, SkipForward, RotateCcw, RotateCw, Maximize,
  Minimize, Maximize2, Minimize2, Square, Circle, Triangle, Hexagon,
  Octagon, Pentagon, Diamond, Star as StarIcon, PieChart as PieChartIcon
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const EmailSettings = () => {
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [testingConnection, setTestingConnection] = useState(false);

  useEffect(() => {
    const fetchEmailAccounts = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getEmailAccounts();
        if (response && response.success && response.data) {
          const accountsData = Array.isArray(response.data) ? response.data : [];
          setEmailAccounts(accountsData);
        } else {
          setError('Failed to load email accounts');
          setEmailAccounts([]);
        }
      } catch (err) {
        setError('Error loading email accounts');
        console.error('Email accounts error:', err);
        setEmailAccounts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmailAccounts();
  }, []);

  const handleCreateAccount = () => {
    setSelectedAccount(null);
    setShowForm(true);
  };

  const handleEditAccount = (account) => {
    setSelectedAccount(account);
    setShowForm(true);
  };

  const handleTestConnection = async (accountId) => {
    setTestingConnection(true);
    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Mock success response
      //console.log(`Testing connection for account ${accountId}`);
    } catch (err) {
      console.error('Connection test failed:', err);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this email account?')) {
      try {
        const updatedAccounts = emailAccounts.filter(account => account.id !== accountId);
        setEmailAccounts(updatedAccounts);
        // In a real app, you would call an API here
        //console.log(`Deleted account ${accountId}`);
      } catch (err) {
        console.error('Failed to delete account:', err);
      }
    }
  };

  const filteredAccounts = (Array.isArray(emailAccounts) ? emailAccounts : []).filter(account => {
    if (!account) return false;
    const accountName = String(account.name || '').toLowerCase();
    const accountEmail = String(account.email || '').toLowerCase();
    const searchLower = String(searchQuery || '').toLowerCase();
    const matchesSearch = accountName.includes(searchLower) || accountEmail.includes(searchLower);
    const matchesStatus = filterStatus === 'all' || account.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'testing': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return Clock;
      case 'error': return AlertTriangle;
      case 'testing': return RefreshCw;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading email accounts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600">{error}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure email accounts for ticket creation and responses
          </p>
        </div>
        <Button onClick={handleCreateAccount} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Email Account
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search email accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Email Accounts List */}
      <div className="grid gap-4">
        {filteredAccounts && filteredAccounts.length > 0 ? filteredAccounts.map((account) => {
          if (!account) return null;
          const StatusIcon = getStatusIcon(account.status || 'inactive');
          return (
            <Card key={account.id || Math.random()} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Mail className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        {account.name || 'Unnamed Account'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {account.email || 'No email address'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getStatusColor(account.status || 'inactive')}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {account.status || 'inactive'}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {account.protocol || 'N/A'} • {account.host || 'N/A'}:{account.port || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConnection(account.id)}
                      disabled={testingConnection}
                    >
                      <TestTube className="h-4 w-4 mr-1" />
                      {testingConnection ? 'Testing...' : 'Test'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditAccount(account)}
                    >
                      <Edit3 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteAccount(account.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Account Details */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Protocol:</span>
                    <p className="font-medium">{account.protocol || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Host:</span>
                    <p className="font-medium">{account.host || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Port:</span>
                    <p className="font-medium">{account.port || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Encryption:</span>
                    <p className="font-medium">{account.encryption || 'N/A'}</p>
                  </div>
                </div>

                {/* Ticket Defaults */}
                {account.ticketDefaults && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ticket Defaults
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Department:</span>
                        <p className="font-medium">{account.ticketDefaults.department || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Priority:</span>
                        <p className="font-medium">{account.ticketDefaults.priority || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Help Topic:</span>
                        <p className="font-medium">{account.ticketDefaults.helpTopic || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        }) : null}
      </div>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-12">
          <Mail className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No email accounts found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || filterStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by adding your first email account.'
            }
          </p>
          {(!searchQuery && filterStatus === 'all') && (
            <Button onClick={handleCreateAccount}>
              <Plus className="h-4 w-4 mr-2" />
              Add Email Account
            </Button>
          )}
        </div>
      )}

      {/* Email Account Form Modal */}
      {showForm && (
        <EmailAccountForm
          account={selectedAccount}
          onClose={() => setShowForm(false)}
          onSave={(accountData) => {
            if (selectedAccount) {
              // Update existing account
              const updatedAccounts = emailAccounts.map(account =>
                account.id === selectedAccount.id ? { ...account, ...accountData } : account
              );
              setEmailAccounts(updatedAccounts);
            } else {
              // Add new account
              const newAccount = {
                id: Date.now().toString(),
                ...accountData,
                status: 'active',
                createdAt: new Date().toISOString()
              };
              setEmailAccounts([...emailAccounts, newAccount]);
            }
            setShowForm(false);
          }}
        />
      )}
    </div>
  );
};

// Email Account Form Component
const EmailAccountForm = ({ account, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: account?.name || '',
    email: account?.email || '',
    username: account?.username || '',
    password: account?.password || '',
    protocol: account?.protocol || 'IMAP',
    host: account?.host || '',
    port: account?.port || '993',
    encryption: account?.encryption || 'SSL/TLS',
    folder: account?.folder || 'INBOX',
    deleteAfterFetch: account?.deleteAfterFetch || false,
    moveToFolder: account?.moveToFolder || false,
    moveFolderName: account?.moveFolderName || '',
    ticketDefaults: {
      department: account?.ticketDefaults?.department || '',
      priority: account?.ticketDefaults?.priority || 'Normal',
      helpTopic: account?.ticketDefaults?.helpTopic || ''
    }
  });

  const [authType, setAuthType] = useState('basic');
  const [testingConnection, setTestingConnection] = useState(false);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Connection test successful!');
    } catch (err) {
      alert('Connection test failed. Please check your settings.');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {account ? 'Edit Email Account' : 'Add Email Account'}
            </h2>
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Authentication Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Authentication Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="basic"
                    checked={authType === 'basic'}
                    onChange={(e) => setAuthType(e.target.value)}
                    className="mr-2"
                  />
                  Basic Authentication
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="oauth"
                    checked={authType === 'oauth'}
                    onChange={(e) => setAuthType(e.target.value)}
                    className="mr-2"
                  />
                  OAuth Authentication
                </label>
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Support Email"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="support@company.com"
                  required
                />
              </div>
            </div>

            {/* Authentication Details */}
            {authType === 'basic' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username *
                  </label>
                  <Input
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="username"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password *
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="password"
                    required
                  />
                </div>
              </div>
            )}

            {authType === 'oauth' && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                  OAuth Authentication
                </p>
                <Button type="button" variant="outline">
                  <Globe className="h-4 w-4 mr-2" />
                  Connect with Google
                </Button>
                <Button type="button" variant="outline" className="ml-2">
                  <Globe className="h-4 w-4 mr-2" />
                  Connect with Microsoft
                </Button>
              </div>
            )}

            {/* Server Configuration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Protocol *
                </label>
                <Select value={formData.protocol} onValueChange={(value) => handleInputChange('protocol', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IMAP">IMAP</SelectItem>
                    <SelectItem value="POP3">POP3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Host *
                </label>
                <Input
                  value={formData.host}
                  onChange={(e) => handleInputChange('host', e.target.value)}
                  placeholder="imap.gmail.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Port *
                </label>
                <Input
                  type="number"
                  value={formData.port}
                  onChange={(e) => handleInputChange('port', e.target.value)}
                  placeholder="993"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Encryption *
                </label>
                <Select value={formData.encryption} onValueChange={(value) => handleInputChange('encryption', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SSL/TLS">SSL/TLS</SelectItem>
                    <SelectItem value="STARTTLS">STARTTLS</SelectItem>
                    <SelectItem value="None">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Folder Configuration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Folder
              </label>
              <Input
                value={formData.folder}
                onChange={(e) => handleInputChange('folder', e.target.value)}
                placeholder="INBOX"
              />
            </div>

            {/* Email Behavior */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Behavior
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.deleteAfterFetch}
                    onChange={(e) => handleInputChange('deleteAfterFetch', e.target.checked)}
                    className="mr-2"
                  />
                  Delete emails after fetching
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.moveToFolder}
                    onChange={(e) => handleInputChange('moveToFolder', e.target.checked)}
                    className="mr-2"
                  />
                  Move emails to folder after fetching
                </label>
                {formData.moveToFolder && (
                  <Input
                    value={formData.moveFolderName}
                    onChange={(e) => handleInputChange('moveFolderName', e.target.value)}
                    placeholder="Processed"
                    className="ml-6"
                  />
                )}
              </div>
            </div>

            {/* Ticket Defaults */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Ticket Defaults
              </label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Department
                  </label>
                  <Select 
                    value={formData.ticketDefaults.department} 
                    onValueChange={(value) => handleInputChange('ticketDefaults.department', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Support">Support</SelectItem>
                      <SelectItem value="Technical">Technical</SelectItem>
                      <SelectItem value="Sales">Sales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Priority
                  </label>
                  <Select 
                    value={formData.ticketDefaults.priority} 
                    onValueChange={(value) => handleInputChange('ticketDefaults.priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Normal">Normal</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Help Topic
                  </label>
                  <Select 
                    value={formData.ticketDefaults.helpTopic} 
                    onValueChange={(value) => handleInputChange('ticketDefaults.helpTopic', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select topic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                      <SelectItem value="Technical Support">Technical Support</SelectItem>
                      <SelectItem value="Billing">Billing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={testingConnection}
              >
                <TestTube className="h-4 w-4 mr-2" />
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button type="submit">
                <Save className="h-4 w-4 mr-2" />
                {account ? 'Update Account' : 'Create Account'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
